import React, { useEffect, useRef, useState } from 'react';
import {
  Grid,
  GridItem,
  MediaLargerEqual,
  color,
  media,
  Text
} from '@morressier/ts';
import styled from 'styled-components';
import { Flex, Spinner } from '@chakra-ui/react';

import { useSidebar } from 'containers/HomeWrapper/hooks';
import { SearchHeadline } from 'components/Search/HomeSearchBar';
import LatestSubmissions from 'containers/LatestSubmissions';
import MetaTags from 'components/MetaTags';
import Container from 'components/Container';
import { logEvent } from 'utils/eventTracking';
import UserEvents from 'components/UserEvents';
import RecentlyVisitedSubmissions from 'components/RecentlyRead';
import ActivitySidebar from 'containers/ActivitySidebar';
import useUnload from 'utils/unloadHook';
import FeaturedProducts from 'components/FeaturedProducts';
import CallForSubmission from 'components/CallForSubmission';

import { AuthContextProps, useAuthContext } from 'context/AuthProvider';
import { useFeatureFlagContext } from 'context/FeatureFlagProvider';
import { checkEnterpriseUser } from 'utils/user';
import { FEATURE_FLAGS } from 'utils/constants';
import httpService from 'services/httpService';
import Dashboard from './dashboard';

enum PaymentType {
  SUBSCRIPTION = 'subscription',
  ONE_TIME = 'one-time'
}

type EventModuleConfig = {
  isAllowed?: boolean;
  product: {
    modules: string[];
  };
  usage_status?: 'linked' | 'unused';
  meta?: Record<string, string>;
};

type EventGroup = {
  unused: string[];
  linked: Record<string, string>;
};

const getReducePredicate =
  (paymentType: PaymentType) =>
  (
    acc: EventGroup,
    { product, isAllowed, usage_status, meta }: EventModuleConfig
  ) => {
    const module = (product?.modules || []).join('+');

    if (isAllowed || paymentType === PaymentType.ONE_TIME)
      return {
        ...acc,
        ...(usage_status === 'unused'
          ? { unused: [...acc.unused, module] }
          : {}),
        ...(usage_status === 'linked'
          ? {
              linked: {
                ...acc.linked,
                ...(meta?.event_id ? { [meta.event_id]: module } : {})
              }
            }
          : {})
      };

    return acc;
  };

const HomePage: React.FC = () => {
  const [purchasedEventModules, setPurchasedEventModules] =
    useState<EventGroup>({ unused: [], linked: {} });
  const user = useAuthContext();
  const firstEventRef = useRef(false); // first HOME_PAGE_VISITED only once
  const features = useFeatureFlagContext();

  const isEnterpriseUser = checkEnterpriseUser(user?.email);

  const isStripePeerReviewSubscriptionEnabled =
    features[FEATURE_FLAGS.STRIPE_PEER_REVIEW];
  const isStripePeerReviewPaymentEnabled =
    features[FEATURE_FLAGS.STRIPE_PEER_REVIEW_ONE_OFF];
  const isTaskOrientedHomeEnabled = features[FEATURE_FLAGS.TASK_ORIENTED_HOME];

  useEffect(() => {
    if (!firstEventRef.current) {
      if (window?.analytics?.ready) {
        window?.analytics?.ready(() => {
          firstEventRef.current = true;
          logEvent('HOME_PAGE_VISITED');
        });
      }
    }
  }, []);

  useSidebar();
  useUnload({
    event: 'HOME_PAGE_PAGE_CLOSED',
    properties: {
      page: 'HOME_PAGE',
      url: window?.location?.href
    }
  });

  useEffect(() => {
    const fetchData = async (url: string, paymentType: PaymentType) => {
      const {
        data: { data }
      } = await httpService.get<{ data: EventModuleConfig[] }>({
        url,
        includeAuth: true
      });

      setPurchasedEventModules(
        data.reduce(getReducePredicate(paymentType), { unused: [], linked: {} })
      );
    };

    if (!isEnterpriseUser) {
      if (isStripePeerReviewPaymentEnabled) {
        fetchData(
          '/product-access-service/payments/me?status=unused,linked',
          PaymentType.ONE_TIME
        );
      } else if (isStripePeerReviewSubscriptionEnabled) {
        fetchData(
          '/product-access-service/subscriptions/me?status=linked',
          PaymentType.SUBSCRIPTION
        );
      }
    }
  }, [isStripePeerReviewSubscriptionEnabled, isStripePeerReviewPaymentEnabled]);

  const renderOldHomePage = () => (
    <Grid gap="24px" xxsmall={1} large={12}>
      <GridItem large={9}>
        <FeaturedProducts
          purchasedEventModules={purchasedEventModules.unused}
          isEnterpriseUser={isEnterpriseUser}
        />
        <LatestSubmissions />
      </GridItem>
      <GridItem large={3}>
        <StickySidebar breakpoint="large" id="sidebar">
          <ActivitySidebar>
            <CallForSubmission />
            <UserEvents
              purchasedEventModules={purchasedEventModules.unused}
              incompleteEvents={purchasedEventModules.linked}
            />
            <RecentlyVisitedSubmissions />
          </ActivitySidebar>
        </StickySidebar>
      </GridItem>
    </Grid>
  );

  if (Object.keys(features).length === 0) {
    return (
      <Flex justifyContent="center">
        <Spinner />
      </Flex>
    );
  }

  return (
    <>
      <MetaTags title="Home" />
      <Search user={user} />
      <LayoutContainer style={{ margin: 'auto' }}>
        {isTaskOrientedHomeEnabled ? (
          <Dashboard user={user} />
        ) : (
          renderOldHomePage()
        )}
      </LayoutContainer>
    </>
  );
};

export default HomePage;

const Search = ({ user }: { user?: AuthContextProps }) => {
  // Extract the first name from the user's full name, defaulting to an empty string
  const [firstName = ''] = user?.full_name?.split(' ') || [];
  const title = `Welcome ${firstName}`;
  return (
    <HomeContainer>
      <div
        style={{
          width: '120rem',
          margin: 'auto',
          paddingLeft: '2rem',
          paddingRight: '2rem'
        }}
      >
        <SearchHeadline>{title}</SearchHeadline>
        <Description size="body1_new">
          Explore our powerful workflows and transform the way you work today.
        </Description>
      </div>
    </HomeContainer>
  );
};

const StickySidebar = styled(MediaLargerEqual)`
  position: sticky;
  top: 75px;
`;

const Description = styled(Text)`
  ${media.xxsmall`
    margin-bottom: 2rem;
  `}
  ${media.medium`
    margin-bottom: 1rem;
  `}
`;

const HomeContainer = styled.div`
  background: ${color('white')};
  background: var(--color-teal-80) url(/home-client/shared/home-header-bg.svg)
    no-repeat 90% -40%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-top: 7rem;
  padding-bottom: 1rem;
  text-align: left;
  ${media.xxsmall`
    margin-top: 2rem;
    padding-left: 1rem;
  `}
  ${media.medium`
    margin-top: 1rem;
    padding-left: 2rem;
  `}
`;

const LayoutContainer = styled(Container)`
  margin-top: 2rem;
`;
